import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth.js";
import { useParams } from "react-router-dom";
import { getUserById } from "../../models/UserFunctions.js"; // Function to fetch user data
import {
  getOrgById,
  getOrgByRef,
  transformLogsToTable,
  transformTransactionsToTable,
} from "../../models/OrgFunctions.js"; // Functions to fetch org and client data
import Divider from "../../layout/Divider.js";
import Space from "../../layout/Space.js";
import DataGridFull from "../../tables/DataGridFull.js";
import { Switch, useTheme } from "@mui/material";
import PrimaryButton from "../../buttons/PrimaryButton.js";
import Icon1 from "../../icons/Icon.js";
import {
  getDocDataById,
  getDocDataByRef,
  getRefFromId,
  queryCollection,
} from "../../models/DataFunctions.js";
import SecondaryButton from "../../buttons/SecondaryButton.js";
import TextField2 from "../../textfields/TextField2.js";
import DropDownGPT0X from "../../dropdowns/DropDownGTP0X.js";
import updateDocDataFieldApi from "../../api/database/updateDocDataFieldApi.js";
import DataGridGPT2 from "../../tables/DataGridGPT2.js";
import { useAppContext } from "../../config/AppContext.js";
import { useSnackbar } from "notistack";

function Transactions({ navigateWorkspace }) {
  const theme = useTheme();
  const { currentUser, loading } = useAuth();

  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const [destination, setDestination] = useState("hexagone");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [options, setOptions] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const [selected_log, setSelectedLog] = useState(null);

  const [transactions, setTransactions] = useState([]);
  // Function to navigate to a client by ID
  const gotoUser = async (log) => {
    const obj = await getDocDataByRef(log.ref);
    setSelectedLog(obj);
  };

  // Function to fetch data for the current user
  const handleFetchData = async (destination) => {
    if (currentUser) {
      // Fetch user data by currentUser's ID
      const user = await getUserById(currentUser.uid);

      // Fetch organization data using the user's org reference
      const org = await getOrgById(user.org_id);
      let transactions = [];
      let filters = [];
      if (destination === "hexagone") {
        filters = [
          {
            field: "destination.type",
            operator: "==",
            value: "hexagone",
          },
        ];
        // Fetch clients associated with the user's organization
        transactions =
          (await queryCollection(
            "transaction",
            filters,
            1000,
            "date",
            "desc"
          )) || [];
      } else if (destination === "master") {
        filters = [
          {
            field: "destination.type",
            operator: "==",
            value: "master",
          },
        ];
        // Fetch clients associated with the user's organization
        transactions =
          (await queryCollection(
            "transaction",
            filters,
            1000,
            "date",
            "desc"
          )) || [];
      } else if (destination === "all") {
        filters = [];
        // Fetch clients associated with the user's organization
        transactions =
          (await queryCollection(
            "transaction",
            filters,
            5000,
            "date",
            "desc"
          )) || [];
      }

      // Transform clients into table format (rows and columns)
      const { rows, columns } = transformTransactionsToTable(
        transactions,
        theme,
        "",
        handleDeactivateTransaction
      );

      // Update state if the user data is new or changed
      if (user && user !== userData) {
        setUserData(user);
        setOrgData(org);
        setTransactions(transactions);
        setRows(rows || []);
        setColumns(columns || []);

        // Set options for the search bar (client names)
        setOptions(transactions.map((transaction) => transaction.product.type));
      }
    }
  };

  const handleDeactivateTransaction = async (id) => {
    let transa = await getDocDataById("transaction", id);
    if (transa) {
      if (!transa.ignored) {
        await updateDocDataFieldApi(transa.ref, "ignored", true, prefix);
        handleClickVariant("Transaction annulée", "success");
      } else {
        await updateDocDataFieldApi(transa.ref, "ignored", false, prefix);
        handleClickVariant("Transaction réactivée", "success");
      }
    } else {
      console.error("Transaction not found");
    }
    handleFetchData(destination);
  };

  const handleSearchChange = (valuee) => {
    let value = searchValue;
    if (valuee) {
      value = valuee;
    } else {
      value = "";
    }

    const { rows, columns } = transformTransactionsToTable(
      transactions,
      theme,
      value,
      handleDeactivateTransaction
    );

    setRows(rows || []);
    setColumns(columns || []);
  };

  // Fetch data when the component mounts or when currentUser changes
  useEffect(() => {
    if (currentUser) {
      handleFetchData(destination);
    }
  }, [currentUser, destination]);

  // If the data is still loading or the user is not authenticated, show a loading message
  if (loading || !currentUser || !userData) {
    return <div></div>;
  }

  // Render the client data
  return (
    <div className="flex flex-col h-screen w-full p-4">
      <div className=" flex flex-row w-full h-full">
        <div className="flex flex-col h-full w-full" y={10}>
          <div className="text-hx font-medium">Transactions</div>
          <Space y={10} />
          <div className="flex flex-row w-full items-center">
            {/* Search Bar */}
            <div className="flex flex-row w-c700  gap-2 items-center">
              <TextField2
                label="Chercher"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <PrimaryButton
                disabled={searchValue == ""}
                className="h-fit "
                onClick={() => handleSearchChange(searchValue)}
              >
                Filtrer
              </PrimaryButton>
              <PrimaryButton
                className="h-fit "
                onClick={() => {
                  setSearchValue("");
                  handleSearchChange(null);
                }}
              >
                Reset
              </PrimaryButton>
            </div>
            <Space x={10} />
            <div className="text-h3 text-shadow_50  flex flex-row gap-1 items-center">
              <DropDownGPT0X
                items={[
                  {
                    value: "hexagone",
                    label: "vers hexagone",
                  },
                  {
                    value: "master",
                    label: "vers masters",
                  },
                  {
                    value: "all",
                    label: "tous",
                  },
                ]}
                value={destination}
                onChange={(e) => setDestination(e.value)}
              ></DropDownGPT0X>
            </div>{" "}
          </div>

          <Space y={0} />

          {/* DataGrid displaying the clients */}
          <div className="w-full h-full pb-8 overflow-auto max-w-c1p4k mb-4 mt-3">
            <div className="h-full  min-h-6">
              <DataGridGPT2
                selectable={false}
                rows={rows}
                columns={columns}
                onClick={() => {}}
                initial_sort_field="date"
                initial_asc="desc"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Transactions;
